import * as React from 'react';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';

type TintColor = 'error' | 'success' | 'info' | 'warning' | 'primary' | 'secondary';

export interface TintedIconButtonProps extends Omit<IconButtonProps, 'color'> {
  color: TintColor;
}

/**
 * An icon button tinted in its colour at rest and a step deeper on hover. A bare MUI IconButton is
 * only a glyph until the pointer finds it; in rows of fields the remove/add/visit controls had
 * nothing marking them as buttons. `theme.alpha` works on the palette CSS variables, so the tint
 * holds in both colour schemes.
 */
export const TintedIconButton = React.forwardRef<HTMLButtonElement, TintedIconButtonProps>(function TintedIconButton(
  { color, sx, ...rest },
  ref
) {
  return (
    <IconButton
      ref={ref}
      color={color}
      sx={[
        (theme) => {
          const main = (theme.vars ?? theme).palette[color].main;
          return {
            bgcolor: theme.alpha(main, 0.12),
            '&:hover': { bgcolor: theme.alpha(main, 0.26) },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    />
  );
}) as React.ForwardRefExoticComponent<TintedIconButtonProps & React.RefAttributes<HTMLButtonElement>> &
  (<C extends React.ElementType>(props: TintedIconButtonProps & { component: C } & Record<string, unknown>) => React.ReactElement);
